<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { useDashboardStore } from './stores/dashboardStore';
import { useRefreshDashboardStore } from './stores/refreshDashboardStore';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useSortByName } from '@/common/composables/sortByName';
import type { Dashboard } from './domain/dashboard';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import MainLayout from '@/common/components/MainLayout.vue';
import { Button, Column, DataTable, Card, InputText, Menu } from 'primevue';
import type { DataTableRowDoubleClickEvent, DataTableRowClickEvent } from 'primevue';
import AuditDialog from 'shared/audit/components/AuditDialog.vue';
import type { MenuItem } from 'primevue/menuitem';
import { useRouter } from 'vue-router';
import { useDashboardAuditApi } from '@/audit/api/dashboardAuditApi';
import DeleteDashboardDialog from './components/DeleteDashboardDialog.vue';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const store = useDashboardStore();
const refreshStore = useRefreshDashboardStore();
const router = useRouter();
const auditApi = useDashboardAuditApi();

const filters = ref({
  global: {
    value: null,
    matchMode: 'contains',
  },
});

const dashbords = useSortByName({
  list: storeToRefs(store).list,
  getName: (e) => e.name,
});

const selected = ref<Dashboard[]>([]);
const loading = ref(false);
const menuEntityId = ref<string>();

const menu = useTemplateRef('menu');
const deleteDialog = useTemplateRef('delete-dialog');
const auditDialog = useTemplateRef('audit-dialog');
const actionFailedAlert = useActionFailedAlert();

onMounted(() => {
  refreshStore.initialize();
});

const menuItems = computed<MenuItem[]>(() => [
  {
    label: translations.value.generic.edit,
    icon: 'pi pi-pencil',
    command() {
      const id = menuEntityId.value;
      if (!id) return;
      show(id);
    },
  },
  {
    label: translations.value.generic.delete,
    icon: 'pi pi-trash',
    command() {
      const id = menuEntityId.value;
      if (!id) return;
      deleteDialog.value?.show(id);
    },
  },
  {
    label: translations.value.generic.audit,
    icon: 'pi pi-search',
    async command() {
      const id = menuEntityId.value;
      if (!id) return;

      const response = await auditApi.execute({
        id: id,
      });

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        return;
      }

      auditDialog.value?.show({
        audit: response.audit,
      });
    },
  },
]);

function onDoubleClick(ev: DataTableRowDoubleClickEvent) {
  const dashboard = ev.data as Dashboard;
  show(dashboard.id);
}

function onClick(ev: DataTableRowClickEvent) {
  const target = ev.originalEvent.target as HTMLElement | null;
  if (!target) return;
  const name = target.closest('.dashboard-name');
  if (!name) return;
  show(ev.data.id);
}

function onAddNewClick() {
  show(null);
}

function show(id: string | null) {
  if (id) {
    router.push('/dashboard/' + id);
  } else {
    router.push('/dashboard');
  }
}

function toggleMenu(ev: Event, id: string) {
  menuEntityId.value = id;
  menu.value?.toggle(ev);
}
</script>

<template>
  <MainLayout>
    <Card class="dashboard-card">
      <template #title>
        <span>{{ translations.dashboard.listPage.title }}</span>
        <Button class="add-button" @click="onAddNewClick" :label="translations.generic.add">
        </Button>
        <InputText class="search-field" autofocus v-model="filters.global.value"
          :placeholder="translations.generic.search" />
      </template>
      <template #content>
        <DataTable class="product-table" sort-field="name" v-model:selection="selected" v-model:filters="filters"
          :value="dashbords" data-key="id" scrollable scrollHeight="flex" meta-key-selection
          @row-dblclick="onDoubleClick" :loading="loading" @row-click="onClick">
          <template #empty>{{ translations.dashboard.listPage.noFound }}</template>
          <template #loading>{{ translations.dashboard.listPage.loading }}</template>
          <Column field="name" class="dashboard-name" :header="translations.generic.name" sortable
            sort-field="nameSortOrder">
          </Column>
          <Column class="w-24 text-end!">
            <template #body="dashboard">
              <Button icon="pi pi-ellipsis-v" variant="text" severity="secondary" rounded
                @click="(ev) => toggleMenu(ev, dashboard.data.id)"></Button>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>
  </MainLayout>

  <DeleteDashboardDialog ref="delete-dialog"></DeleteDashboardDialog>
  <AuditDialog ref="audit-dialog"></AuditDialog>
  <Menu ref="menu" :model="menuItems" :popup="true"></Menu>
</template>

<style scoped>
:deep(.p-datatable-hoverable .p-datatable-selectable-row) {
  cursor: unset;
}

:deep(.p-card-title) {
  display: grid;
  grid-template-columns: auto 1fr auto 1rem auto;
  column-gap: 0.5rem;
  align-items: center;
}

.add-button {
  grid-column: 3;
}

.search-field {
  grid-column: 5;
}

:deep(.dashboard-name) {
  cursor: pointer;
}

:deep(.dashboard-name:hover) {
  color: var(--p-primary-900);
  background-color: var;
  text-decoration: underline;
}

.dashboard-card {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-body) {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-content) {
  overflow-y: hidden;
  height: 100%;
}
</style>
